
import { useCallback, useEffect, useState } from 'react';
import {
  useNodesState,
  useEdgesState,
  addEdge,
  Connection
} from '@xyflow/react';
import { GamePlan } from '@/types/gamePlanner';
import { nanoid } from 'nanoid';

const nodeTypes = ['positionNode', 'techniqueNode', 'conditionalNode'];

export function useFlowState(initialPlan: GamePlan | null) {
  const initialNodes = initialPlan?.flow_data?.nodes || [];
  const initialEdges = initialPlan?.flow_data?.edges || [];
  
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialNodesLength] = useState(initialNodes.length);
  const [initialEdgesLength] = useState(initialEdges.length);
  
  // Track changes by comparing current state with initial state
  useEffect(() => {
    const nodesChanged = nodes.length !== initialNodesLength;
    const edgesChanged = edges.length !== initialEdgesLength;
    
    if (nodesChanged || edgesChanged) {
      setHasChanges(true);
    }
  }, [nodes, edges, initialNodesLength, initialEdgesLength]);
  
  const onConnect = useCallback(
    (connection: Connection) => setEdges((eds) => addEdge(connection, eds)),
    [setEdges]
  );
  
  const addNode = useCallback(
    (type: string) => {
      if (!nodeTypes.includes(type)) {
        console.warn(`Invalid node type: ${type}`);
        return;
      }
      
      const newNode = {
        id: `${type}_${nanoid()}`,
        type,
        position: { x: Math.random() * 300, y: Math.random() * 300 },
        data: { label: `${type.charAt(0).toUpperCase() + type.slice(1)}` }
      };
      

      setNodes((nds) => [...nds, newNode]);
      setHasChanges(true);
    },
    [setNodes]
  );
  
  return {
    nodes,
    edges,
    hasChanges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addNode,
  };
}
